import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';
import { imageUrl } from '../lib/images';

export default function Lagerhotel() {
  return (
    <>
      <SEO
        title="Lagerhotel - fleksibel kundetilpasset service"
        description="1800 m2 tørt, lyst, frostfrit og rent Lagerhotel i Esbjerg med læsserampe, truck og kameraovervågning,"
        keywords="lagerhotel, pluk og pak, emballering, godsmodtagelse, godshåndtering, tømning af container, logistik, godsdistribution, cross docking, toldoplag, opmagasinering"
        canonicalUrl="https://fastlane.dk/lager"
      />

      <PageHeader
        title="Lagerhotel, Distribution og Logistik"
        subtitle="1800 m² tørt, lyst, frostfrit og rent lagerhotel i Esbjerg med læsserampe, truck og kameraovervågning."
        breadcrumbs={[
          { label: 'Ydelser', path: '/godstransport-i-europa' },
          { label: 'Lagerhotel & Logistik' }
        ]}
      />

      <div className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          {/* Hero image for warehouse */}
          <div className="w-fit max-w-full overflow-hidden rounded-2xl shadow-sm border border-slate-200 bg-slate-100">
            <img
              src={imageUrl('https://assets.auxodev.dk/assets/2e6d0d1c-821d-400c-80aa-275c6c0c86cc/fastlane-indg-lager.jpg', 'width=1200,quality=85,format=auto')}
              alt="Fastlane Lagerhotel i Esbjerg"
              className="max-h-[350px] w-auto max-w-full object-cover block"
            />
          </div>

          {/* Lagerhotel Esbjerg */}
          <div className="space-y-4 text-slate-700 leading-relaxed text-base sm:text-lg">
            <h2 className="text-2xl font-bold text-[#0f2b48]">Lagerhotel Esbjerg</h2>
            <p>
              Traditionel oplagring af gods i kortere og længere perioder. Vores 1800 m2 lagerhotel i Esbjerg er lyst, tørt, rent og frostfrit. Udstyret med hydraulisk læsserampe, truck, alarmsystem, kameraovervågning, lagerreoler, folieringsmaskine m.m.
            </p>
            <p>
              Udover oplagring af gods tilbyder vi relaterede services tilpasset dine behov.
            </p>
            <p>
              Hos os er det speditørerne som håndtere godset på lagerhotellet, hvilket sikre en præcis kommunikation og høj kvalitet, idet vi er med i hele flowet. Fuld integritet mellem kundens forventninger og den service som leveres fra vores side.
            </p>
          </div>

          {/* Feature Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
            {/* Logistikcenter */}
            <div className="p-6 bg-slate-50 rounded-xl border border-slate-200/90 space-y-2">
              <div className="w-10 h-10 rounded-lg bg-sky-100 text-sky-800 flex items-center justify-center mb-3">
                <i className="fa-solid fa-boxes-packing text-lg" aria-hidden="true" />
              </div>
              <h3 className="text-lg font-bold text-[#0f2b48]">Logistikcenter</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Vi tilbyder: Godshåndtering, emballering, pakning, plukning og mærkning af godset. Visuel kvalitetskontrol af gods og godsmængder ved varemodtagelse f.eks. i forbindelse med tømning af containere. Herunder ankomstrapport med fejl/mangler m.v.
              </p>
            </div>

            {/* Kørsel og Tømning af containere */}
            <div className="p-6 bg-slate-50 rounded-xl border border-slate-200/90 space-y-2">
              <div className="w-10 h-10 rounded-lg bg-sky-100 text-sky-800 flex items-center justify-center mb-3">
                <i className="fa-solid fa-container-storage text-lg" aria-hidden="true" />
              </div>
              <h3 className="text-lg font-bold text-[#0f2b48]">Kørsel og Tømning af containere</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Vi har stor erfaring med tømning af containere på vegne af vore kunder på lagerhotellet i Esbjerg. Tilbyder også en totalløsning, hvor vi står for kørsel fra havn fortoldning, tømning af container m.m.{' '}
                <Link to="/koersel-af-importcontainere" className="text-sky-700 font-semibold underline block mt-2">
                  Containerkørsel & toldklarering af gods
                </Link>
              </p>
            </div>

            {/* Distributionscenter */}
            <div className="p-6 bg-slate-50 rounded-xl border border-slate-200/90 space-y-2">
              <div className="w-10 h-10 rounded-lg bg-sky-100 text-sky-800 flex items-center justify-center mb-3">
                <i className="fa-solid fa-truck-ramp-box text-lg" aria-hidden="true" />
              </div>
              <h3 className="text-lg font-bold text-[#0f2b48]">Distributionscenter</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Vi distribuerer gods i Danmark, Europa og øvrige verden.
              </p>
            </div>

            {/* Toldoplag - Bonded Warehouse */}
            <div className="p-6 bg-slate-50 rounded-xl border border-slate-200/90 space-y-2">
              <div className="w-10 h-10 rounded-lg bg-sky-100 text-sky-800 flex items-center justify-center mb-3">
                <i className="fa-solid fa-shield-halved text-lg" aria-hidden="true" />
              </div>
              <h3 className="text-lg font-bold text-[#0f2b48]">Toldoplag - Bonded Warehouse</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Vi har et Toldoplag, hvor kunder kan oplægge importvarer uden at betale told. Fordele:
              </p>
              <ul className="list-disc pl-5 text-xs text-slate-600 space-y-1">
                <li>positiv likviditet</li>
                <li>mulighed for reeksport til 3. land f.eks. Norge, Schweiz eller USA uden tab af indbetalt told</li>
              </ul>
            </div>

            {/* Online Lagerstyring - Webstock */}
            <div className="p-6 bg-slate-50 rounded-xl border border-slate-200/90 space-y-2">
              <div className="w-10 h-10 rounded-lg bg-sky-100 text-sky-800 flex items-center justify-center mb-3">
                <i className="fa-solid fa-desktop text-lg" aria-hidden="true" />
              </div>
              <h3 className="text-lg font-bold text-[#0f2b48]">Online Lagerstyring - Webstock</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Online lagerlister, så vore kunder (med brugernavn og kodeord) til enhver tid kan se deres aktuelle lagerstand. Enkle gennemprøvede løsninger, der anvender standard programmer som kan vedligeholdes tilpasses til meget lave priser f.eks. via TEAMS, DROPBOX, ONE DRIVE eller lignende.
              </p>
            </div>

            {/* Emballering */}
            <div className="p-6 bg-slate-50 rounded-xl border border-slate-200/90 space-y-2">
              <div className="w-10 h-10 rounded-lg bg-sky-100 text-sky-800 flex items-center justify-center mb-3">
                <i className="fa-solid fa-box text-lg" aria-hidden="true" />
              </div>
              <h3 className="text-lg font-bold text-[#0f2b48]">Emballering</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Vi pakker og emballerer på vegne af kunder med udgangspunkt i godsets beskaffenhed, værdi og den transportform som vælges at benytte.
              </p>
            </div>

            {/* Cross Docking */}
            <div className="p-6 bg-slate-50 rounded-xl border border-slate-200/90 space-y-2">
              <div className="w-10 h-10 rounded-lg bg-sky-100 text-sky-800 flex items-center justify-center mb-3">
                <i className="fa-solid fa-dolly text-lg" aria-hidden="true" />
              </div>
              <h3 className="text-lg font-bold text-[#0f2b48]">Cross Docking</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Vi tilbyder: Modtagelseskontrol, ommærking af varer, tilpasning af varer til kundespecifikke formål m.m.
              </p>
            </div>

            {/* Kundekontor */}
            <div className="p-6 bg-slate-50 rounded-xl border border-slate-200/90 space-y-2">
              <div className="w-10 h-10 rounded-lg bg-sky-100 text-sky-800 flex items-center justify-center mb-3">
                <i className="fa-solid fa-building-user text-lg" aria-hidden="true" />
              </div>
              <h3 className="text-lg font-bold text-[#0f2b48]">Kundekontor</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Vi har mulighed for, at stille kontorarbejdsplads evt. helt kontor til rådighed i længere eller kortere perioder for kunder som bruger vort lager.
              </p>
            </div>
          </div>

          {/* Contact prompt */}
          <div className="p-6 sm:p-8 bg-sky-50 rounded-2xl border border-sky-200 flex flex-col sm:flex-row items-center justify-between gap-6">
            <div>
              <p className="text-slate-800 text-base font-medium">
                Basis en detaljeret afklaring af dine behov tilbyder vi en god løsning og pris på din lagerforretning.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto shrink-0">
              <a
                href="mailto:booking@fastlane.dk"
                className="w-full sm:w-auto inline-flex items-center justify-center px-5 py-2.5 rounded-lg bg-[#0f2b48] hover:bg-sky-900 text-white text-sm font-semibold transition-colors text-center"
              >
                E-mail os
              </a>
              <a
                href="tel:+4576121230"
                className="w-full sm:w-auto inline-flex items-center justify-center px-5 py-2.5 rounded-lg bg-white border border-slate-300 text-slate-900 text-sm font-semibold hover:bg-slate-50 transition-colors whitespace-nowrap text-center"
              >
                +45 76 12 12 30
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
